import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AddBowlerModal from './AddBowlerModal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PAGE_SIZE = 20;

interface Bowler { id: number; name: string; email: string; }
interface GroupMember { id: number; name: string; email: string; source: 'sideaction' | 'event'; set_average?: number | null; }

const PaginationBar: React.FC<{
  page: number; totalPages: number; total: number;
  onPage: (p: number) => void; loading?: boolean;
}> = ({ page, totalPages, total, onPage, loading }) => (
  <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-700 bg-gray-800/40 flex-shrink-0">
    <span className="text-xs text-gray-500">{total.toLocaleString()} total</span>
    <div className="flex items-center gap-1">
      <button onClick={() => onPage(1)} disabled={page <= 1 || loading}
        className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-30 rounded text-gray-300 transition-colors">«</button>
      <button onClick={() => onPage(page - 1)} disabled={page <= 1 || loading}
        className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-30 rounded text-gray-300 transition-colors">‹</button>
      <span className="text-xs text-gray-400 px-2">{page} / {totalPages}</span>
      <button onClick={() => onPage(page + 1)} disabled={page >= totalPages || loading}
        className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-30 rounded text-gray-300 transition-colors">›</button>
      <button onClick={() => onPage(totalPages)} disabled={page >= totalPages || loading}
        className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-30 rounded text-gray-300 transition-colors">»</button>
    </div>
  </div>
);

const ClearButton: React.FC<{ onClear: () => void }> = ({ onClear }) => (
  <button onClick={onClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  </button>
);

const CreateBowlerGroups: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();

  // ── Left: all bowlers (server-side paginated) ─────────────────────────────
  const [allSearch, setAllSearch] = useState('');
  const [allBowlers, setAllBowlers] = useState<Bowler[]>([]);
  const [allPage, setAllPage] = useState(1);
  const [allTotal, setAllTotal] = useState(0);
  const [allTotalPages, setAllTotalPages] = useState(1);
  const [loadingAll, setLoadingAll] = useState(false);
  const [addingId, setAddingId] = useState<number | null>(null);
  const [showAddBowler, setShowAddBowler] = useState(false);
  const [bowlerRefetchKey, setBowlerRefetchKey] = useState(0);

  // ── Pull-from-league modal ────────────────────────────────────────────────
  interface SourceLeague { id: number; name: string; bowler_count: number; }
  interface PullPreview { newBowlers: { id: number; name: string; email: string }[]; alreadyIn: { id: number; name: string; email: string }[]; }
  const [showPullModal, setShowPullModal] = useState(false);
  const [sourceLeagues, setSourceLeagues] = useState<SourceLeague[]>([]);
  const [loadingSourceLeagues, setLoadingSourceLeagues] = useState(false);
  const [selectedSource, setSelectedSource] = useState<SourceLeague | null>(null);
  const [pullPreview, setPullPreview] = useState<PullPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pullResult, setPullResult] = useState<{ added: number } | null>(null);

  // ── Right: group members (client-side filtered + paginated) ───────────────
  const [group, setGroup] = useState<GroupMember[]>([]);
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [groupFilter, setGroupFilter] = useState('');
  const [groupPage, setGroupPage] = useState(1);  const [avgInput, setAvgInput] = useState<Record<number, string>>({});
  const [savingAvg, setSavingAvg] = useState<Record<number, boolean>>({});
  // ── Fetch group ───────────────────────────────────────────────────────────
  const loadGroup = useCallback(() => {
    if (!leagueId) return;
    setLoadingGroup(true);
    axios.get(`${API}/bookie-league-management/leagues/${leagueId}/bowler-group`, { headers: authHeader() })
      .then(r => {
        const members: GroupMember[] = r.data || [];
        setGroup(members);
        const initAvg: Record<number, string> = {};
        for (const m of members) {
          initAvg[m.id] = m.set_average != null ? String(m.set_average) : '';
        }
        setAvgInput(initAvg);
      })
      .catch(() => {})
      .finally(() => setLoadingGroup(false));
  }, [leagueId]);

  useEffect(() => { loadGroup(); }, [loadGroup]);

  // ── Fetch all bowlers (debounced on search, immediate on page change) ─────
  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    setLoadingAll(true);
    const delay = allSearch.trim() ? 300 : 0;
    const t = setTimeout(() => {
      const params = new URLSearchParams({ page: String(allPage), limit: String(PAGE_SIZE) });
      if (allSearch.trim()) params.set('query', allSearch.trim());
      axios.get(`${API}/bookie-league-management/leagues/${leagueId}/all-bowlers?${params}`, { headers: authHeader() })
        .then(r => {
          if (cancelled) return;
          setAllBowlers(r.data.bowlers || []);
          setAllTotal(r.data.total || 0);
          setAllTotalPages(r.data.totalPages || 1);
        })
        .catch(() => {})
        .finally(() => { if (!cancelled) setLoadingAll(false); });
    }, delay);
    return () => { cancelled = true; clearTimeout(t); };
  }, [allSearch, allPage, leagueId, bowlerRefetchKey]);

  // Reset group page when filter changes
  useEffect(() => { setGroupPage(1); }, [groupFilter]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAllSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAllSearch(e.target.value);
    setAllPage(1);
  };

  const addToGroup = async (bowler: Bowler) => {
    setAddingId(bowler.id);
    try {
      await axios.post(
        `${API}/bookie-league-management/leagues/${leagueId}/bowler-group`,
        { bowlerId: bowler.id }, { headers: authHeader() }
      );
      loadGroup();
    } catch { } finally { setAddingId(null); }
  };

  const removeFromGroup = async (bowlerId: number) => {
    setRemovingId(bowlerId);
    try {
      await axios.delete(
        `${API}/bookie-league-management/leagues/${leagueId}/bowler-group/${bowlerId}`,
        { headers: authHeader() }
      );
      setGroup(g => g.filter(m => m.id !== bowlerId));
    } catch { } finally { setRemovingId(null); }
  };

  const saveAverage = async (bowlerId: number) => {
    const val = avgInput[bowlerId];
    if (val === '' || isNaN(Number(val))) return;
    setSavingAvg(prev => ({ ...prev, [bowlerId]: true }));
    try {
      await axios.put(
        `${API}/bookie-league-management/leagues/${leagueId}/bowler-group/${bowlerId}/set-average`,
        { set_average: Number(val) },
        { headers: authHeader() }
      );
      setGroup(g => g.map(m => m.id === bowlerId ? { ...m, set_average: Number(val) } : m));
    } catch { } finally {
      setSavingAvg(prev => ({ ...prev, [bowlerId]: false }));
    }
  };

  // ── Pull-from-league handlers ─────────────────────────────────────────────
  const openPullModal = async () => {
    setShowPullModal(true);
    setSelectedSource(null);
    setPullPreview(null);
    setPullResult(null);
    setLoadingSourceLeagues(true);
    try {
      const r = await axios.get(
        `${API}/bookie-league-management/leagues/${leagueId}/bowler-group/source-leagues`,
        { headers: authHeader() }
      );
      setSourceLeagues(r.data || []);
    } catch { setSourceLeagues([]); }
    finally { setLoadingSourceLeagues(false); }
  };

  const selectSourceLeague = async (league: SourceLeague) => {
    setSelectedSource(league);
    setPullPreview(null);
    setPullResult(null);
    setLoadingPreview(true);
    try {
      const r = await axios.get(
        `${API}/bookie-league-management/leagues/${leagueId}/bowler-group/source-leagues/${league.id}/preview`,
        { headers: authHeader() }
      );
      setPullPreview(r.data);
    } catch { setPullPreview(null); }
    finally { setLoadingPreview(false); }
  };

  const confirmPull = async () => {
    if (!selectedSource) return;
    setPulling(true);
    try {
      const r = await axios.post(
        `${API}/bookie-league-management/leagues/${leagueId}/bowler-group/pull-from-league`,
        { sourceLeagueId: selectedSource.id },
        { headers: authHeader() }
      );
      setPullResult({ added: r.data.added });
      loadGroup(); // refresh the right panel
    } catch { }
    finally { setPulling(false); }
  };

  const closePullModal = () => {
    setShowPullModal(false);
    setSelectedSource(null);
    setPullPreview(null);
    setPullResult(null);
  };

  // ── Derived: filtered + paginated group ──────────────────────────────────
  const filteredGroup = group.filter(m => {
    if (!groupFilter.trim()) return true;
    const q = groupFilter.toLowerCase();
    return (m.name ?? '').toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q) || String(m.id).includes(q);
  });
  const groupTotalPages = Math.max(1, Math.ceil(filteredGroup.length / PAGE_SIZE));
  const groupPaged = filteredGroup.slice((groupPage - 1) * PAGE_SIZE, groupPage * PAGE_SIZE);
  const groupIds = new Set(group.map(m => m.id));

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center gap-3 flex-shrink-0">
        <button onClick={() => navigate(-1)}
          className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-base font-bold text-white">Bowler Groups</h1>
          <p className="text-xs text-gray-400">Manage your league bowler group for quick SE entry search</p>
        </div>
        <button onClick={() => setShowAddBowler(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
          + New Bowler to System
        </button>
        <button onClick={openPullModal}
          className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg transition-colors">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Pull from League
        </button>
      </div>

      {/* Two columns */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left: Players in Bowler Pro ── */}
        <div className="w-1/2 flex flex-col border-r border-gray-700 overflow-hidden">
          <div className="px-5 py-4 bg-gray-800/60 border-b border-gray-700 flex-shrink-0">
            <h2 className="text-sm font-bold text-gray-200 mb-3">Players in Bowler Pro</h2>
            <div className="relative">
              <input value={allSearch} onChange={handleAllSearchChange}
                placeholder="Filter by name, ID or email..."
                className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500" />
              {allSearch && <ClearButton onClear={() => { setAllSearch(''); setAllPage(1); }} />}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5">
            {loadingAll && (
              <div className="flex justify-center py-8">
                <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {!loadingAll && allBowlers.length === 0 && (
              <div className="text-center py-12 text-gray-500 text-sm">No bowlers found</div>
            )}
            {!loadingAll && allBowlers.map(b => {
              const inGroup = groupIds.has(b.id);
              return (
                <div key={b.id} className="flex items-center justify-between bg-gray-800/60 border border-gray-700/60 rounded-lg px-3 py-2.5">
                  <div className="min-w-0 mr-3">
                    <div className="text-white text-sm font-medium truncate">{b.name}</div>
                    <div className="text-gray-400 text-xs truncate">{b.email}</div>
                    <div className="text-gray-600 text-[10px]">ID #{b.id}</div>
                  </div>
                  {inGroup ? (
                    <span className="flex-shrink-0 text-xs text-purple-300 bg-purple-900/30 border border-purple-700/40 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      In Group
                    </span>
                  ) : (
                    <button onClick={() => addToGroup(b)} disabled={addingId === b.id}
                      className="flex-shrink-0 flex items-center gap-1.5 text-xs px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg font-semibold transition-colors whitespace-nowrap">
                      {addingId === b.id && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                      Add to League
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <PaginationBar page={allPage} totalPages={allTotalPages} total={allTotal} onPage={setAllPage} loading={loadingAll} />
        </div>

        {/* ── Right: Bowlers in this League ── */}
        <div className="w-1/2 flex flex-col overflow-hidden">
          <div className="px-5 py-4 bg-gray-800/60 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-sm font-bold text-gray-200">Bowlers in this League</h2>
              <span className="text-xs text-gray-500">
                {group.length} total
                {group.filter(m => m.source === 'event').length > 0 && (
                  <span className="ml-2 text-blue-400">· {group.filter(m => m.source === 'event').length} in events</span>
                )}
              </span>
            </div>
            <div className="relative">
              <input value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
                placeholder="Verify a bowler is in the group..."
                className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500" />
              {groupFilter && <ClearButton onClear={() => setGroupFilter('')} />}
            </div>
            {groupFilter.trim() && (
              <p className="text-xs text-gray-500 mt-1.5">{filteredGroup.length} match{filteredGroup.length !== 1 ? 'es' : ''}</p>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5">
            {loadingGroup && (
              <div className="flex justify-center py-8">
                <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {!loadingGroup && filteredGroup.length === 0 && (
              <div className="text-center py-12 text-gray-500 text-sm">
                {groupFilter.trim() ? 'No bowlers match your search.' : (
                  <>No bowlers in group yet.<br /><span className="text-gray-600">Add them from the left column.</span></>
                )}
              </div>
            )}
            {!loadingGroup && groupPaged.map(m => (
              <div key={m.id}
                className={`flex items-center justify-between rounded-lg px-3 py-2.5 border ${
                  m.source === 'event' ? 'bg-blue-900/20 border-blue-700/40' : 'bg-gray-800/60 border-gray-700/60'
                }`}>
                <div className="flex items-center gap-2.5 min-w-0 flex-1 mr-2">
                  <div className="min-w-0">
                    <div className="text-white text-sm font-medium truncate">{m.name}</div>
                    <div className="text-gray-400 text-xs truncate">{m.email}</div>
                    <div className="text-gray-600 text-[10px]">ID #{m.id}</div>
                  </div>
                  {m.source === 'event' && (
                    <span className="flex-shrink-0 text-[10px] px-2 py-0.5 bg-blue-700/40 text-blue-300 border border-blue-600/40 rounded-full whitespace-nowrap">
                      Playing
                    </span>
                  )}
                </div>
                {/* Right actions: avg input + trash */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <input
                    type="number"
                    min={0} max={300} step={0.5}
                    value={avgInput[m.id] ?? ''}
                    onChange={e => setAvgInput(prev => ({ ...prev, [m.id]: e.target.value }))}
                    onFocus={e => e.target.select()}
                    placeholder="avg"
                    title="Set starting average"
                    className="w-14 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs text-center focus:outline-none focus:border-purple-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                  {avgInput[m.id] !== '' && String(m.set_average ?? '') !== avgInput[m.id] && (
                    <button
                      onClick={() => saveAverage(m.id)}
                      disabled={savingAvg[m.id]}
                      title="Save average"
                      className="p-1 text-green-400 hover:text-green-300 disabled:opacity-50 transition-colors">
                      {savingAvg[m.id]
                        ? <div className="w-3.5 h-3.5 border border-green-400 border-t-transparent rounded-full animate-spin" />
                        : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                      }
                    </button>
                  )}
                  {m.source === 'sideaction' && (
                    <button onClick={() => removeFromGroup(m.id)} disabled={removingId === m.id}
                      title="Remove from group"
                      className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50">
                      {removingId === m.id
                        ? <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                        : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                      }
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <PaginationBar page={groupPage} totalPages={groupTotalPages} total={filteredGroup.length} onPage={setGroupPage} loading={loadingGroup} />
        </div>

      </div>

      {showAddBowler && (
        <AddBowlerModal
          onClose={() => setShowAddBowler(false)}
          onCreated={() => setBowlerRefetchKey(k => k + 1)}
        />
      )}

      {/* ── Pull from League modal ─────────────────────────────────────── */}
      {showPullModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh]">

            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 flex-shrink-0">
              <div>
                <h2 className="text-base font-bold text-white">Pull Bowlers from a League</h2>
                <p className="text-xs text-gray-400 mt-0.5">Pick an admin-managed league and import all its registered members into your bowler group.</p>
              </div>
              <button onClick={closePullModal} className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex flex-1 overflow-hidden min-h-0">

              {/* Left: league list */}
              <div className="w-1/2 border-r border-gray-700 flex flex-col overflow-hidden">
                <div className="px-4 py-3 bg-gray-800/40 border-b border-gray-700 flex-shrink-0">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Select a League</p>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
                  {loadingSourceLeagues ? (
                    <div className="flex justify-center py-8">
                      <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : sourceLeagues.length === 0 ? (
                    <div className="text-center py-10 text-gray-500 text-sm">
                      No active leagues with registered members found.
                    </div>
                  ) : (
                    sourceLeagues.map(lg => (
                      <button
                        key={lg.id}
                        onClick={() => selectSourceLeague(lg)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
                          selectedSource?.id === lg.id
                            ? 'bg-indigo-900/40 border-indigo-500/60 text-white'
                            : 'bg-gray-800/60 border-gray-700/60 text-gray-300 hover:bg-gray-800 hover:text-white'
                        }`}
                      >
                        <div className="text-sm font-medium leading-snug">{lg.name}</div>
                        <div className="text-xs text-indigo-400 mt-0.5">{lg.bowler_count} registered member{lg.bowler_count !== 1 ? 's' : ''}</div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Right: preview pane */}
              <div className="w-1/2 flex flex-col overflow-hidden">
                <div className="px-4 py-3 bg-gray-800/40 border-b border-gray-700 flex-shrink-0">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {selectedSource ? `Preview — ${selectedSource.name}` : 'Preview'}
                  </p>
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                  {!selectedSource && (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 text-sm text-center gap-2 py-8">
                      <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>Select a league to preview its bowlers</span>
                    </div>
                  )}
                  {selectedSource && loadingPreview && (
                    <div className="flex justify-center py-8">
                      <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {selectedSource && !loadingPreview && pullPreview && !pullResult && (
                    <div className="space-y-3">
                      {/* Summary chips */}
                      <div className="flex gap-2">
                        <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-900/40 text-emerald-300 border border-emerald-700/40 font-semibold">
                          +{pullPreview.newBowlers.length} new
                        </span>
                        {pullPreview.alreadyIn.length > 0 && (
                          <span className="text-xs px-2.5 py-1 rounded-full bg-gray-700/60 text-gray-400 border border-gray-600/40 font-semibold">
                            {pullPreview.alreadyIn.length} already in group
                          </span>
                        )}
                      </div>
                      {pullPreview.newBowlers.length === 0 ? (
                        <p className="text-sm text-gray-400 py-2">All bowlers from this league are already in your group.</p>
                      ) : (
                        <div className="space-y-1">
                          {pullPreview.newBowlers.map(b => (
                            <div key={b.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-emerald-900/20 border border-emerald-800/30">
                              <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                              <div className="min-w-0">
                                <div className="text-white text-xs font-medium truncate">{b.name}</div>
                                <div className="text-gray-500 text-[10px] truncate">{b.email}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {pullResult && (
                    <div className="flex flex-col items-center justify-center h-full text-center gap-3 py-8">
                      <div className="w-12 h-12 rounded-full bg-emerald-900/40 border border-emerald-500/40 flex items-center justify-center">
                        <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-white font-semibold text-sm">
                        {pullResult.added > 0
                          ? `${pullResult.added} bowler${pullResult.added !== 1 ? 's' : ''} added to your group!`
                          : 'All bowlers were already in your group.'}
                      </p>
                      <button onClick={closePullModal}
                        className="mt-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs font-semibold rounded-lg transition-colors">
                        Done
                      </button>
                    </div>
                  )}
                </div>

                {/* Confirm button (only shown when there are new bowlers to add) */}
                {selectedSource && !loadingPreview && pullPreview && !pullResult && pullPreview.newBowlers.length > 0 && (
                  <div className="px-4 py-3 border-t border-gray-700 flex-shrink-0">
                    <button
                      onClick={confirmPull}
                      disabled={pulling}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-bold rounded-xl transition-colors"
                    >
                      {pulling
                        ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Adding…</>
                        : <>Import {pullPreview.newBowlers.length} bowler{pullPreview.newBowlers.length !== 1 ? 's' : ''}</>
                      }
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateBowlerGroups;

